import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import BlockIcon from '@mui/icons-material/Block';
import ReplayIcon from '@mui/icons-material/Replay';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { Box } from "@mui/system";

import { observer } from "mobx-react-lite";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { Client, ClientRole } from "../../../data/models/client";
import { useStore } from "../../../stores/store";
import { Pagination } from "../../../data/models/pagination";
import DropdownMenu from "../../../components/DropdownMenu";
import GenericTable, { SortType } from "../../../components/GenericTable";
import { TableHeader } from "../../../_styles/table/TableHeader";
import { TableText } from "../../../_styles/table/TableText";
import { Container, IconButton, Link, Tooltip } from "@mui/material";
import { arrayToString, isUUID } from "../../../utils/utils";
import { CheckIcon } from "../../../_styles/icons/CheckIcon";
import "../styles.css";
import { useTranslation } from "react-i18next";
import { RoundButton } from "../../../_styles/StyledButtons";
import { hasOtherRoles, hasRoles } from "../../../utils/permissionEvaluator";
import ClientRoleFilter from "./ClientRoleFilter";
import UserStatusFilter from "./UserStatusFilter";
import UserStatusChip from "../../../components/UserStatusChip";
import { UserDeactivationRequest, UserStatus } from "../../../data/models/user";
import { AppRoles } from "../../../data/models/role";
import UsersListActionDialog from "../Form/UsersListActionDialog";
import ExportConsentFiles from "../ExportConsentFiles/ExportConsentFiles";
interface ClientProps {
    onSort?: (key: string, type: SortType) => void;
    onPageChange?: (page: number, pageSize: number) => void;
    onRoleChange: (roleId: number) => void;
    onStatusChange: (status: string) => void;
    handleChange: (filterChange: boolean) => Promise<void>
}

function ClientList({ onSort, onPageChange, onRoleChange, onStatusChange, handleChange }: ClientProps) {
    const { t } = useTranslation();
    const {
        clientStore: { loading, deleteClient, clientList },
        userStore: { reactivateUser, deactivateUser, anonymizeUser },
        dialogStore: { openDialog, closeDialog },
        institutionStore: { selectedUserInstitution },
        dataExportStore: { getBridgeTableExport, getConsentFiles },
        loadingStore: { isLoading },
        modalStore: { openModal }
    } = useStore();
    const paginationString = sessionStorage.getItem("client-pagination");
    const paginationParams = paginationString
        ? JSON.parse(paginationString!)
        : null;
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        pageSize: paginationParams?.PageSize ?? 10,
    });
    const maxPage = useRef(pagination.page);
    const navigate = useNavigate();

    let options: {
        id: string;
        name: string;
        icon: JSX.Element;
        onClick: (id: string, clientRoles: ClientRole[]) => void;
        customStyle: {
            color: string;
        };
    }[] = [];

    if (hasOtherRoles(['CLIENT', 'HELPER'], selectedUserInstitution?.roles!)) {
        options = [
            ...options,
            {
                id: "edit",
                name: t("GENERAL_EDIT"),
                icon: (
                    <EditIcon
                        sx={{
                            position: "absolute",
                            right: 0,
                            paddingRight: 1,
                            color: "#118ab2",
                        }}
                    />
                ),
                onClick: (id: string) => navigate(`/users/${id}/update`),
                customStyle: {
                    color: "#118ab2",
                },
            },
        ];
    }

    if (selectedUserInstitution?.roles.some((r) => r.toUpperCase() === "ADMIN")) {
        options = [
            ...options,
            {
                id: "delete",
                name: t("GENERAL_DELETE"),
                onClick: (id: string, clientRoles: ClientRole[]) => {
                    openDialog(
                        t("DIALOG_TITLE_DELETE"),
                        t("DIALOG_MSG_CLIENT"),
                        async (userDeactivateRequest: UserDeactivationRequest) => {
                            await deleteClient(id, userDeactivateRequest);
                            closeDialog();
                            toast.success(t("TOAST_CLIENT_DELETE"));
                        },
                        closeDialog,
                        undefined,
                        true,
                        undefined,
                        undefined,
                        "reason"

                    )
                },
                icon: (
                    <DeleteIcon
                        sx={{ position: "absolute", right: 0, paddingRight: 1 }}
                        color="error"
                    />
                ),
                customStyle: {
                    color: "#FF0000",
                },
            },
            {
                id: "deactivate",
                name: t("DEACTIVATE"),
                icon: (
                    <BlockIcon
                        sx={{ position: "absolute", right: 0, paddingRight: 1 }}
                        color="primary"
                    />
                ),
                onClick: (id: string, clientRoles: ClientRole[]) => {
                    openDialog(
                        t("DIALOG_TITLE_DEACTIVATE_USER"),
                        t("DIALOG_MSG_DEACTIVATE_USER"),
                        async (userDeactivateRequest: UserDeactivationRequest) => {
                            await deactivateUser(id, selectedUserInstitution?.institutionId as string, userDeactivateRequest);
                            closeDialog();
                            toast.success(t("TOAST_USER_DEACTIVATED"));
                            await handleChange(true);
                        },
                        closeDialog,
                        {
                            text: t("DEACTIVATE"),
                            color: "error",
                        },
                        true,
                        undefined,
                        undefined,
                        "reason"
                    )
                },
                customStyle: {
                    color: "#6ca0b7",
                },
            },
            {
                id: "reactivate",
                name: t("REACTIVATE"),
                icon: (
                    <ReplayIcon
                        sx={{ position: "absolute", right: 0, paddingRight: 1 }}
                        color="primary"
                    />
                ),
                onClick: (id: string, clientRoles: ClientRole[]) => {
                    openDialog(
                        t("DIALOG_TITLE_REACTIVATE_USER"),
                        t("DIALOG_MSG_REACTIVATE_USER"),
                        async (userDeactivateRequest: UserDeactivationRequest) => {
                            await reactivateUser(id, selectedUserInstitution?.institutionId as string, userDeactivateRequest);
                            closeDialog();
                            toast.success(t("TOAST_USER_REACTIVATED"));
                            await handleChange(true);
                        },
                        closeDialog,
                        {
                            text: t("REACTIVATE"),
                            color: "warning",
                        },
                        true,
                        undefined,
                        undefined,
                        "reason"
                    )
                },
                customStyle: {
                    color: "#6ca0b7",
                },
            },
            {
                id: "anonymize",
                name: t("ANONYMIZE"),
                icon: (
                    <SecurityOutlinedIcon
                        sx={{ position: "absolute", right: 0, paddingRight: 1 }}
                        color="primary"
                    />
                ),
                onClick: (id: string, clientRoles: ClientRole[]) => {
                    openDialog(
                        t("DIALOG_TITLE_ANONYMIZE_USER"),
                        t("DIALOG_MSG_ANONYMIZE_USER"),
                        async (userDeactivateRequest: UserDeactivationRequest) => {
                            await anonymizeUser(id, selectedUserInstitution?.institutionId as string, userDeactivateRequest);
                            closeDialog();
                            toast.success(t("TOAST_USER_ANONYMIZED"));
                            await handleChange(true);
                        },
                        closeDialog,
                        {
                            text: t("ANONYMIZE"),
                            color: "error",
                        },
                        true,
                        undefined,
                        undefined,
                        "reason"
                    )
                },
                customStyle: {
                    color: "#6ca0b7",
                },
            },
        ];
    }

    const exportBridgeTable = async () => {
        if (!selectedUserInstitution) return;
        try {
            const data = await getBridgeTableExport(
                selectedUserInstitution.institutionId
            );

            const blob = new Blob([data], { type: "text/csv" });

            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `result2.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error exporting data:", error);
        }
    };

    const handlePageChange = async (page: number, pageSize: number) => {
        setPagination({ page: page + 1, pageSize });
        maxPage.current = page + 1;

        if (onPageChange) {
            onPageChange(page, pageSize);
        }
    };

    return (
        <Container maxWidth="xl">
            <GenericTable<Client>
                columns={[
                    {
                        header: <TableHeader>{t("GENERAL_NAME")}</TableHeader>,
                        key: "fullName",
                        render: (client) => (

                            <TableText>
                                {client.roles.find((r) => r.name === AppRoles.CLIENT) ?
                                    <Tooltip title={t('GENERAL_VIEW_DETAILS')}>
                                        <Link
                                            component="button"
                                            underline='hover'
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/users/${client.id}`)}
                                        >
                                            {client.firstName} {client.lastName}
                                        </Link>
                                    </Tooltip> : <>{client.firstName} {client.lastName}</>
                                }
                            </TableText>
                        ),
                        className: "process-cell",
                        sort: (a, b) => {
                            const aFullName = `${a.firstName} ${a.lastName}`;
                            const bFullName = `${b.firstName} ${b.lastName}`;
                            return aFullName
                                .toLowerCase()
                                .localeCompare(bFullName.toLowerCase());
                        },
                    },
                    {
                        header: <TableHeader>{t("USER_EMAIL")}</TableHeader>,
                        key: "email",
                        render: (client) => (
                            <TableText>{isUUID(client.email) ? "" : client.email}</TableText>
                        ),
                        className: "process-cell",
                        sort: (a, b) => {
                            const isAUUID = isUUID(a.email);
                            const isBUUID = isUUID(b.email);

                            if (isAUUID && isBUUID) {
                                return 0;
                            } else if (isAUUID) {
                                return -1;
                            } else if (isBUUID) {
                                return 1;
                            } else {
                                return a.email
                                    .toLowerCase()
                                    .localeCompare(b.email.toLowerCase());
                            }
                        },
                    },
                    {
                        header: <TableHeader>{t("SETTINGS_ROLES_NAME")}</TableHeader>,
                        key: "role",
                        render: (client) => (
                            <TableText
                                title={arrayToString(client.roles.map((r) => r.name))}
                                sx={{ whiteSpace: "normal", overflow: "auto" }}
                            >
                                {arrayToString(client.roles.map((r) => r.name))}
                            </TableText>
                        ),
                        className: "process-cell",
                    },
                    {
                        header: (
                            <TableHeader sx={{ display: "flex", justifyContent: "center" }}>
                                {t("STATUS")}
                            </TableHeader>
                        ),
                        key: "status",
                        render: (client) => (
                            <Box>
                                <UserStatusChip status={client.status} />
                            </Box>
                        ),
                        className: "process-cell",
                        width: 10,
                    },
                    {
                        header: (
                            <TableHeader sx={{ display: "flex", justifyContent: "center" }}>
                                {t("ACC_LABEL")}
                            </TableHeader>
                        ),
                        key: "hasAccount",
                        render: (client) => (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    ml: "15px",
                                }}
                            >
                                {client.hasAccount && <CheckIcon />}
                            </Box>
                        ),
                        className: "process-cell",
                        width: 10,
                    },
                    {
                        header: <TableHeader></TableHeader>,
                        key: "actions",
                        render: (client) =>
                            client.roles.find((r) => r.name === "Client") ? (
                                <Box mr={2}>
                                    {client.status === UserStatus.PROSPECT ? (
                                        <DropdownMenu
                                            options={options
                                                .filter(option => ['edit', 'delete'].includes(option.id))
                                                .map(option => ({
                                                    ...option,
                                                    onClick: () => option.onClick(client.id, client.roles),
                                                }))}
                                        />
                                    ) : options
                                        .filter(option => !(
                                            ([UserStatus.ACTIVE, UserStatus.ANON].includes(client.status) && option.id === 'reactivate') ||
                                            ([UserStatus.DEACTIVATED, UserStatus.ANON].includes(client.status) && option.id === 'deactivate') ||
                                            (client.status === UserStatus.ANON && (option.id === 'anonymize' || option.id === 'edit'))
                                        )).length === 0 ? (
                                        <div style={{ marginRight: "16px" }}>
                                            <IconButton disabled>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </div>
                                    ) : (
                                        <DropdownMenu
                                            options={options
                                                .filter(option => !(
                                                    ([UserStatus.ACTIVE, UserStatus.ANON].includes(client.status) && option.id === 'reactivate') ||
                                                    ([UserStatus.DEACTIVATED, UserStatus.ANON].includes(client.status) && option.id === 'deactivate') ||
                                                    (client.status === UserStatus.ANON && (option.id === 'anonymize' || option.id === 'edit'))
                                                ))
                                                .map(option => ({
                                                    ...option,
                                                    onClick: () => option.onClick(client.id, client.roles),
                                                }))}
                                        />
                                    )
                                    }
                                </Box>
                            ) : (
                                <Tooltip
                                    title={t("USER_GO_TO_SETTINGS_TO_EDIT")}
                                    placement="bottom"
                                >
                                    <div style={{ marginRight: "16px" }}>
                                        <IconButton disabled>
                                            <MoreVertIcon />
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            ),
                        className: "client-list-actions",
                    },
                ]}
                rows={clientList || []}
                props={{
                    containerProps: { className: "survey-table-container" },
                    tableContainerProps: { className: "survey-table-container-data" },
                    tableHeadProps: { className: "survey-table-header" },
                    rowProps: { className: "survey-row" },
                }}
                rowCount={paginationParams?.TotalCount ?? clientList.length}
                rowKey="id"
                loading={loading}
                usePagination
                onPaginationModelChange={handlePageChange}
                onSortChange={onSort}
                toolbar={
                    <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} width={200}>
                            <ClientRoleFilter onFilterChange={onRoleChange} />
                            <UserStatusFilter onFilterChange={onStatusChange} />
                        </Box>
                        {hasRoles(
                            ["Contact Manager"],
                            selectedUserInstitution?.roles ?? [],
                            false
                        ) ? (
                            <RoundButton
                                variant="text"
                                onClick={exportBridgeTable}
                                disabled={isLoading(getBridgeTableExport)}
                                color="info"
                                disableRipple
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 400,
                                    mr: 2,
                                    ml: 2,
                                    '&:hover': {
                                        textDecoration: "underline"
                                    }
                                }}
                            >
                                {isLoading(getBridgeTableExport)
                                    ? "Exporting"
                                    : "Export bridge data table"}
                            </RoundButton>
                        ) : null}
                        {hasRoles(
                            ["Contact Manager"],
                            selectedUserInstitution?.roles ?? [],
                            false
                        ) ? (
                            <RoundButton
                                variant="text"
                                onClick={() => openModal(
                                    <ExportConsentFiles />,
                                    { size: "xs", confirmClose: false }
                                )}
                                disabled={isLoading(getConsentFiles)}
                                color="info"
                                disableRipple
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 400,
                                    mr: 2,
                                    ml: 2,
                                    '&:hover': {
                                        textDecoration: "underline"
                                    }
                                }}
                            >
                                {isLoading(getBridgeTableExport)
                                    ? "Exporting"
                                    : "Export clients consent files"}
                            </RoundButton>
                        ) : null}
                    </Box>
                }
            />
            < UsersListActionDialog />
        </Container>
    );
}

export default observer(ClientList);
